import { useCallback, useEffect, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Check, ChevronDown, ChevronRight, History, Plus, ShoppingCart, Store } from "lucide-react";
import { flushSync } from "react-dom";
import { de, estimateOpen, groupByCategory, type CategoryKey, type Item, type Receipt, type ShopKey } from "@ainkauf/core";
import { SyncBadge } from "@/components/SyncBadge";
import { EmptyList } from "@/components/EmptyList";
import { AllDone } from "@/components/AllDone";
import { QuickAdd } from "@/components/QuickAdd";
import { Konfetti } from "@/components/Konfetti";
import { ItemRow } from "@/components/ItemRow";
import { ShopSheet } from "@/components/ShopSheet";
import { UndoToast } from "@/components/UndoToast";
import { ItemSheet } from "@/components/ItemSheet";
import { FinishSheet } from "@/components/FinishSheet";
import { BottomNav } from "@/components/BottomNav";
import { MicButton } from "@/components/MicButton";
import { zettelGewaehlt } from "@/lib/assistant-events";
import { vorWieLange } from "@/lib/relative-time";
import {
  addItem,
  addItemsSortiert,
  createList,
  allMembers,
  allProducts,
  allStores,
  allTrips,
  categoryOrderForList,
  defaultList,
  deleteItem,
  deleteList,
  ensureDefaultList,
  finishTrip,
  itemsOfList,
  listById,
  setItemChecked,
  shoppingRightNow,
  undoCheck,
  updateItem,
  updateList,
} from "@/db/repository";
import { useNavigate } from "@/lib/router";
import { activeUserId } from "@/lib/session";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

/** Die Liste selbst: der Bildschirm, den die Familie am häufigsten sieht. */
export function ListScreen({ listId }: { listId?: string }) {
  const go = useNavigate();

  // Live-Queries lesen nur. Das Anlegen läuft im Effekt daneben, sonst
  // schreibt die Oberfläche in einer Nur-Lese-Transaktion von Dexie.
  // Ohne Vorgabe: undefined heißt "lädt noch", null heißt "keine Liste".
  const listeGeladen = useLiveQuery(async () => (listId ? listById(listId) : defaultList()), [listId]);
  const list = listeGeladen ?? null;
  useEffect(() => {
    if (listId === undefined) void ensureDefaultList();
  }, [listId]);

  // Die Liste, zu der die Artikel gehören, kommt mit: So lässt sich "diese
  // Liste ist wirklich leer" von "noch nicht geladen" unterscheiden.
  const geladen = useLiveQuery(
    async () => ({ listId: list?.id ?? null, items: list ? await itemsOfList(list.id) : [] }),
    [list?.id],
    { listId: undefined as string | null | undefined, items: [] as Item[] },
  );
  const items = geladen.items;
  const order = useLiveQuery(async () => categoryOrderForList(list), [list?.id, list?.store_id], [] as CategoryKey[]);
  const stores = useLiveQuery(() => allStores(), [], []);
  const letzterEinkauf = useLiveQuery(async () => (await allTrips())[0]?.finished_at ?? null, [], null);
  const produkte = useLiveQuery(() => allProducts(), [], []);
  const mitglieder = useLiveQuery(() => allMembers(), [], []);
  const unterwegs = useLiveQuery(() => shoppingRightNow(), [], null);

  const [showDone, setShowDone] = useState(false);
  /*
    Die Eingabe ist eingeklappt, bis jemand auf Plus tippt. Daniel am
    17.10.2026: Die Zeile "Artikel hinzufügen" kostete auf jedem Besuch Platz,
    gebraucht wird sie nur beim Anlegen. Eine leere Liste zeigt sie von selbst,
    und wer einmal angelegt hat, behält sie offen, bis er sie zuklappt.
  */
  const [eingabeOffen, setEingabeOffen] = useState(false);
  const feld = useRef<HTMLInputElement>(null);
  // Vor dem ersten Abgleich gibt es noch gar keine Liste; auch das ist leer.
  const wirklichLeer = listeGeladen !== undefined && geladen.listId === (list?.id ?? null) && items.length === 0;
  const zeigeEingabe = eingabeOffen || wirklichLeer;
  const [fragtNachBetrag, setFragtNachBetrag] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [undo, setUndo] = useState<{ item: Item; message: string } | null>(null);
  const [feiert, setFeiert] = useState(false);
  // Ein Artikel, der woanders hingehört, für den es aber keine Liste gibt.
  // Er ist noch nicht angelegt; das passiert erst mit der Antwort.
  const [frage, setFrage] = useState<{ name: string; shop: ShopKey; raw: string } | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  function melde(text: string) {
    setHinweis(text);
    window.setTimeout(() => {
      setHinweis(null);
    }, 4000);
  }

  // Wer ist wer: für "von Ben" an fremden Haken und "Anna kauft gerade ein".
  const ich = activeUserId();
  const namen = new Map(mitglieder.map((mitglied) => [mitglied.user_id, mitglied.display_name]));
  const nameVon = (userId: string | null) => (userId === null || userId === ich ? null : (namen.get(userId) ?? de.family.someone));

  const offen = items.filter((item) => !item.is_checked);
  const erledigt = items.filter((item) => item.is_checked);
  const abschnitte = groupByCategory(offen, order);
  const laden = stores.find((store) => store.id === list?.store_id) ?? null;

  // Was die offenen Artikel vermutlich kosten. Auf einem dauerhaften Board ist
  // das die nützlichere Zahl als ein Fortschritt: Sie sagt, was noch aussteht.
  const schaetzung = estimateOpen(items, produkte);

  /*
    Konfetti genau im Moment des Leerwerdens: die Liste hatte Artikel, und der
    letzte offene ist gerade weggefallen. Der Vergleich läuft über eine Ref,
    damit ein bloßes Neuzeichnen (etwa nach einem Sync) nicht noch einmal
    feiert, und ein Wechsel auf eine schon leere Liste gar nicht erst.
  */
  const warOffen = useRef(0);
  useEffect(() => {
    if (warOffen.current > 0 && offen.length === 0 && items.length > 0) setFeiert(true);
    warOffen.current = offen.length;
  }, [offen.length, items.length]);

  const onAdd = useCallback(
    async (raw: string) => {
      // Wer anlegt, will meist gleich weiter anlegen: Die Eingabe bleibt offen,
      // auch wenn die Liste dadurch nicht mehr leer ist.
      setEingabeOffen(true);
      // Steht die Liste in der Adresse, gehört der Artikel dorthin, auch
      // wenn ihre Zeile noch nicht angekommen ist. Sonst die Standardliste,
      // notfalls neu angelegt, statt die Eingabe zu verschlucken.
      const zielId = list?.id ?? listId ?? (await ensureDefaultList(true))?.id;
      if (!zielId) return;

      // Sortiert nur aus der Standardliste heraus: Wer eine Liste gezielt
      // öffnet, meint sie auch (siehe addItemsSortiert).
      const ergebnis = await addItemsSortiert(zielId, raw);

      if (ergebnis.umgeleitet.length === 1) {
        const [eine] = ergebnis.umgeleitet;
        if (eine) melde(de.items.movedToList(eine.name, eine.liste));
      } else if (ergebnis.umgeleitet.length > 1) {
        const listen = new Set(ergebnis.umgeleitet.map((eintrag) => eintrag.liste));
        const erste = ergebnis.umgeleitet[0];
        melde(
          listen.size === 1 && erste
            ? de.items.movedMany(ergebnis.umgeleitet.length, erste.liste)
            : de.items.movedSeveral(ergebnis.umgeleitet.length),
        );
      }

      // Immer nur eine Frage auf einmal: Wer drei Sachen diktiert, von denen
      // zwei keine Liste haben, soll nicht zwei Blätter nacheinander wegtippen.
      const offen = ergebnis.ohneListe[0];
      if (offen) setFrage(offen);
    },
    [list, listId],
  );

  // Der Hinweis merkt sich die geschriebene Zeile, nicht die alte: Rückgängig
  // prüft daran, ob der Haken noch der eigene ist (undoCheck).
  const onToggle = useCallback(async (item: Item) => {
    const geschrieben = await setItemChecked(item.id, !item.is_checked);
    setUndo(geschrieben?.is_checked ? { item: geschrieben, message: de.items.checkedToast(item.name) } : null);
  }, []);

  const onFinish = useCallback(
    async (totalCents: number | null, receipt: Receipt | null) => {
      if (!list) return;
      await finishTrip(list.id, { storeId: list.store_id, totalCents, receipt });
      setUndo(null);
      setFragtNachBetrag(false);
    },
    [list],
  );

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-3 px-4 mit-leiste">
      <header className="flex flex-col gap-2 pt-3">
        {/*
          Die Kopfkarte, so knapp wie möglich: oben der Name der Liste, der
          Stand der Übertragung und der Knopf zum Anlegen, darunter in einer
          Zeile, was ein Board beantworten muss. Die Begrüßung nach Tageszeit
          ist am 17.10.2026 entfallen, sie war die einzige Zeile ohne
          Information. Der warme Verlauf hebt die Karte vom Grund ab, ohne eine
          zweite weiße Fläche zu sein.
        */}
        <div
          className="auftritt relative overflow-hidden rounded-[var(--radius-xl)] py-2.5 pl-3.5 pr-2.5"
          style={{
            /*
              Zwei weiche Lichtflecken über einem warmen Verlauf. Erst waren es
              zwei weiße Kreise, aber deren harte Kanten sahen aus wie ein
              Fleck. Als Radialverläufe laufen sie aus, und die Fläche wirkt
              beleuchtet statt beklebt.
            */
            background:
              "radial-gradient(120% 90% at 82% -20%, rgb(255 255 255 / 0.72) 0%, transparent 62%), " +
              "radial-gradient(90% 80% at 0% 120%, rgb(255 255 255 / 0.5) 0%, transparent 60%), " +
              "linear-gradient(135deg, #fff6ec 0%, var(--accent-soft) 52%, var(--honey-soft) 100%)",
            boxShadow: "var(--shadow-card)",
          }}
        >
          <div className="relative flex items-center gap-2">
            {list ? (
              <input
                value={list.name}
                onChange={(event) => {
                  void updateList(list.id, { name: event.target.value });
                }}
                aria-label={de.lists.rename}
                data-testid="list-name"
                className="-ml-1 min-w-0 flex-1 truncate rounded-[var(--radius-sm)] bg-transparent px-1 font-display text-[1.5rem] leading-tight font-extrabold tracking-tight text-ink outline-none"
              />
            ) : (
              <h1 className="min-w-0 flex-1 truncate text-[1.5rem] leading-tight">{de.lists.defaultName}</h1>
            )}

            <SyncBadge />

            {/* Sprechen ist der zweite Weg zum Anlegen, deshalb gleich daneben,
                aber leiser als das Plus. */}
            <MicButton currentListId={list?.id ?? null} />

            {/* Der wichtigste Knopf der Seite, deshalb in Tomate. Er klappt die
                Eingabe auf und fokussiert sie noch im selben Tipp: Nur dann
                öffnet iOS die Tastatur. */}
            <button
              type="button"
              onClick={() => {
                if (zeigeEingabe) {
                  setEingabeOffen(false);
                  return;
                }
                flushSync(() => {
                  setEingabeOffen(true);
                });
                feld.current?.focus();
              }}
              aria-expanded={zeigeEingabe}
              aria-label={zeigeEingabe ? de.items.quickAddClose : de.items.quickAddOpen}
              data-testid="quick-add-open"
              className="knopf-haupt inline-flex size-11 shrink-0 items-center justify-center rounded-full transition-transform active:scale-90"
            >
              <Plus
                size={24}
                strokeWidth={2.5}
                aria-hidden
                className="transition-transform duration-200"
                style={{ transform: zeigeEingabe ? "rotate(45deg)" : "none" }}
              />
            </button>
          </div>

          {/*
            Kein Fortschritt und kein Prozentsatz: Diese Liste ist ein
            dauerhaftes Board, keine Aufgabe mit Ende. Hier steht stattdessen,
            was ein Board beantworten muss: wie viel offen ist, für welchen
            Laden, und wann zuletzt eingekauft wurde.
          */}
          <p className="num relative mt-1.5 flex flex-wrap items-center gap-1 text-[12px]">
            <span data-testid="open-count" className="rounded-full bg-white/70 px-2 py-0.5 font-semibold text-ink-2">
              {de.items.openCount(offen.length)}
            </span>
            {laden !== null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 font-semibold text-ink-2">
                <Store size={12} strokeWidth={2.25} aria-hidden />
                {laden.name}
              </span>
            )}
            {schaetzung.known > 0 && (
              <span
                data-testid="open-estimate"
                title={de.spending.estimated}
                className="rounded-full bg-white/70 px-2 py-0.5 font-semibold text-ink-2"
              >
                {de.spending.estimatedShort(euro(schaetzung.totalCents))}
              </span>
            )}
            {/* Steht jemand gerade im Laden, ist das die wichtigste
                Zeile auf dem Board: Jetzt noch schnell etwas eintippen. */}
            {unterwegs !== null && (
              <span
                data-testid="someone-shopping"
                className="inline-flex items-center gap-1 rounded-full bg-herb px-2 py-0.5 font-semibold text-white"
              >
                <ShoppingCart size={12} strokeWidth={2.25} aria-hidden />
                {de.trip.someoneShopping(nameVon(unterwegs.started_by) ?? de.family.someone)}
              </span>
            )}
            {letzterEinkauf !== null && (
              <span
                data-testid="last-trip"
                className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 font-semibold text-ink-2"
              >
                <History size={12} strokeWidth={2.25} aria-hidden />
                {de.trip.lastTrip(vorWieLange(letzterEinkauf))}
              </span>
            )}
          </p>
        </div>

        {zeigeEingabe && (
          <div className="auftritt">
            <QuickAdd onAdd={onAdd} onPhoto={zettelGewaehlt} feldRef={feld} />
          </div>
        )}
      </header>

      <main className="flex flex-1 flex-col gap-3">
        {offen.length === 0 ? (
          items.length === 0 ? (
            <EmptyList />
          ) : (
            <AllDone />
          )
        ) : (
          /*
            Sortiert nach Warengruppe, aber ohne Überschriften: Das Zeichen vorn
            in jeder Zeile sagt dasselbe, und die Überschriften kosteten je
            Gruppe eine Zeile. Daniel am 17.10.2026: "Mir reicht es, wenn ich
            vorne dieses Icon habe." Etwas Luft zwischen den Gruppen genügt.
          */
          abschnitte.map((abschnitt) => (
            <section key={abschnitt.key} data-testid="list-group" data-name={abschnitt.label}>
              <ul className="flex flex-col gap-1.5">
                {abschnitt.items.map((item, i) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    onToggle={onToggle}
                    onEdit={setEditing}
                    onToggleSale={(zeile) => void updateItem(zeile.id, { only_on_sale: zeile.only_on_sale !== true })}
                    takt={Math.min(i * 26, 260)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}

        {offen.length > 0 && list && (
          <button
            type="button"
            onClick={() => {
              go(`/einkauf/${list.id}`);
            }}
            data-testid="start-shopping"
            className="knopf-haupt inline-flex min-h-[56px] items-center justify-center gap-2 text-[17px]"
          >
            <ShoppingCart size={19} strokeWidth={2.25} aria-hidden />
            {de.trip.start}
          </button>
        )}

        {erledigt.length > 0 && (
          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDone((value) => !value);
                }}
                aria-expanded={showDone}
                data-testid="toggle-done"
                className="flex min-h-[44px] items-center gap-1.5 rounded-full px-2 text-sm font-semibold text-ink-2"
              >
                {showDone ? <ChevronDown size={17} strokeWidth={2.25} aria-hidden /> : <ChevronRight size={17} strokeWidth={2.25} aria-hidden />}
                <span className="num">{de.items.doneCount(erledigt.length)}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setFragtNachBetrag(true);
                }}
                data-testid="finish-trip"
                className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-herb px-4 text-sm font-semibold text-white transition-transform active:scale-95"
              >
                <Check size={16} strokeWidth={2.5} aria-hidden />
                {de.trip.finish}
              </button>
            </div>

            {showDone && (
              <ul className="flex flex-col gap-1.5 opacity-70">
                {erledigt.map((item) => (
                  <ItemRow key={item.id} item={item} onToggle={onToggle} checkedByName={nameVon(item.checked_by)} />
                ))}
              </ul>
            )}
          </section>
        )}

        {listId !== undefined && list && (
          <div className="flex justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                go("/listen");
              }}
              data-testid="back-to-lists"
              className="min-h-[44px] rounded-full px-3 text-sm text-ink-2"
            >
              {de.common.back}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(de.lists.deleteConfirm(list.name))) {
                  void deleteList(list.id).then(() => {
                    go("/listen");
                  });
                }
              }}
              data-testid="delete-list"
              className="min-h-[44px] rounded-full px-3 text-sm text-ink-2"
            >
              {de.lists.delete}
            </button>
          </div>
        )}
      </main>

      <BottomNav active="listen" />

      {fragtNachBetrag && (
        <FinishSheet
          onFinish={onFinish}
          onCancel={() => {
            setFragtNachBetrag(false);
          }}
        />
      )}

      {editing && (
        <ItemSheet
          item={editing}
          onSave={async (patch) => {
            await updateItem(editing.id, patch);
          }}
          onDelete={async () => {
            await deleteItem(editing.id);
          }}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}

      {feiert && (
        <Konfetti
          seed={items.length}
          onDone={() => {
            setFeiert(false);
          }}
        />
      )}

      {frage && (
        <ShopSheet
          name={frage.name}
          shop={frage.shop}
          standardliste={list?.name ?? de.lists.defaultName}
          onCreate={() => {
            const offen = frage;
            setFrage(null);
            void (async () => {
              const neueListe = await createList(de.shops.namen[offen.shop]);
              await addItem(neueListe.id, offen.raw);
              melde(de.items.movedToList(offen.name, neueListe.name));
            })();
          }}
          onKeep={() => {
            const offen = frage;
            setFrage(null);
            void (async () => {
              const zielId = list?.id ?? listId;
              if (zielId) await addItem(zielId, offen.raw);
            })();
          }}
        />
      )}

      {hinweis && (
        <p
          role="status"
          data-testid="list-hinweis"
          // pointer-events-none: Der Hinweis informiert nur, er hat keinen
          // Knopf. Ohne das fängt er Tipps auf die Artikel ab, die unter ihm
          // liegen, und zwar vier Sekunden lang.
          className="pointer-events-none fixed inset-x-4 bottom-24 z-30 rounded-[var(--radius-lg)] bg-herb/15 px-4 py-3 text-center text-sm font-semibold text-herb"
        >
          {hinweis}
        </p>
      )}

      {undo && (
        <UndoToast
          message={undo.message}
          onUndo={() => {
            void undoCheck(undo.item.id, undo.item.checked_at ?? "");
            setUndo(null);
          }}
          onDismiss={() => {
            setUndo(null);
          }}
        />
      )}
    </div>
  );
}
